import type { ApiPage, ApiResponse } from './types.js'
import { inspectedFetch } from '../index.js'

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] as string | undefined
const TOKEN_STORAGE_KEY = 'autonome-web:accessToken'
const USERNAME_STORAGE_KEY = 'autonome-web:username'
const ROLE_STORAGE_KEY = 'autonome-web:role'

export type SessionRole = 'operator' | 'facility_administrator' | 'worker'

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function readStoredUsername(): string | null {
  try {
    return sessionStorage.getItem(USERNAME_STORAGE_KEY)
  } catch {
    return null
  }
}

function readStoredRole(): SessionRole | null {
  try {
    const value = sessionStorage.getItem(ROLE_STORAGE_KEY)
    return value === 'operator' || value === 'facility_administrator' || value === 'worker' ? value : null
  } catch {
    return null
  }
}

let accessToken: string | null = readStoredToken()
let username: string | null = readStoredUsername()
let role: SessionRole | null = readStoredRole()

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string): void {
  accessToken = token
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token)
  } catch {
    // sessionStorage unavailable (private browsing, etc.) - token still works for this render.
  }
}

export function getUsername(): string | null {
  return username
}

export function setUsername(value: string): void {
  username = value
  try {
    sessionStorage.setItem(USERNAME_STORAGE_KEY, value)
  } catch {
    // sessionStorage unavailable - username still works for this render.
  }
}

export function getRole(): SessionRole | null {
  return role
}

export function setRole(value: SessionRole): void {
  role = value
  try {
    sessionStorage.setItem(ROLE_STORAGE_KEY, value)
  } catch {
    // sessionStorage unavailable - role still works for this render.
  }
}

export function clearAccessToken(): void {
  accessToken = null
  username = null
  role = null
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
    sessionStorage.removeItem(USERNAME_STORAGE_KEY)
    sessionStorage.removeItem(ROLE_STORAGE_KEY)
  } catch {
    // ignore
  }
}

const sessionEndedListeners = new Set<() => void>()

/**
 * Tells the shells when the API stopped accepting the session's token - it expired (a demo session lasts an hour,
 * a sign-in its own term) or was revoked - so they can say so instead of offering a Try again that cannot work.
 * Returns the unsubscribe.
 */
export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener)
  return () => {
    sessionEndedListeners.delete(listener)
  }
}

/** A 401 on a signed-in call: the token is gone, and whoever was listening is told once. */
function endSession(): void {
  const hadSession = accessToken !== null
  clearAccessToken()
  if (hadSession) {
    sessionEndedListeners.forEach((listener) => listener())
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** The API's whole answer, for a refusal that says more than its message (a release held back names its findings). */
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** True for a 404 from a React Query `error` field - the API's signal for "this facility exists but hasn't been configured yet," not a real failure. */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra }
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  return headers
}

async function getEnvelope<T>(path: string): Promise<ApiResponse<T> & { data: T }> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const response = await inspectedFetch(`${API_BASE_URL}${path}`, { headers: authHeaders() })
  const body = await readEnvelope<T>(response, path)

  if (response.status === 401) {
    endSession()
  }

  if (!response.ok || !body.success || body.data === undefined) {
    throw new ApiError(body.error ?? `Request to ${path} failed`, response.status, body)
  }

  return { ...body, data: body.data }
}

export async function apiGet<T>(path: string): Promise<T> {
  return (await getEnvelope<T>(path)).data
}

/**
 * One page of a paged list: the rows, the cursor for the next page and the
 * list's total where the API gives one. `path` carries `limit` and any
 * filter; `cursor` is the previous page's `nextCursor`, or null for the
 * first page.
 */
export async function apiGetPage<T>(path: string, cursor: string | null): Promise<ApiPage<T>> {
  const withCursor = cursor === null ? path : `${path}${path.includes('?') ? '&' : '?'}cursor=${encodeURIComponent(cursor)}`
  const body = await getEnvelope<T[]>(withCursor)
  return { items: body.data, nextCursor: body.meta?.nextCursor ?? null, total: body.meta?.total ?? null }
}

/** A paged answer that is an object rather than a list of rows: the object, and the cursor for its next page. */
export async function apiGetWithCursor<T>(path: string): Promise<{ data: T; nextCursor: string | null }> {
  const body = await getEnvelope<T>(path)
  return { data: body.data, nextCursor: body.meta?.nextCursor ?? null }
}

/**
 * The API always answers with a JSON envelope; anything else is the
 * gateway or a deployment that lacks the route (Hono's plain "404 Not
 * Found", say). Naming that is far more useful than a JSON parse error.
 */
export async function readEnvelope<T>(response: Response, path: string): Promise<ApiResponse<T>> {
  const text = await response.text()
  try {
    return JSON.parse(text) as ApiResponse<T>
  } catch {
    throw new ApiError(
      `${path} answered ${response.status} without a JSON body${response.status === 404 ? ' - this endpoint may not be deployed to the API yet' : ''}`,
      response.status,
    )
  }
}

async function apiMutate<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, payload: unknown): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const response = await inspectedFetch(`${API_BASE_URL}${path}`, {
    method,
    headers: authHeaders(payload === undefined ? undefined : { 'content-type': 'application/json' }),
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
  const body = await readEnvelope<T>(response, path)

  if (response.status === 401) {
    endSession()
  }

  if (!response.ok || !body.success || body.data === undefined) {
    throw new ApiError(body.error ?? `Request to ${path} failed`, response.status, body)
  }

  return body.data
}

export function apiPut<T>(path: string, payload: unknown): Promise<T> {
  return apiMutate<T>('PUT', path, payload)
}

export function apiPost<T>(path: string, payload: unknown): Promise<T> {
  return apiMutate<T>('POST', path, payload)
}

export function apiPatch<T>(path: string, payload: unknown): Promise<T> {
  return apiMutate<T>('PATCH', path, payload)
}

/** For a DELETE that answers with a JSON body (the state after the delete) rather than 204. */
export function apiDeleteWithResult<T>(path: string): Promise<T> {
  return apiMutate<T>('DELETE', path, undefined)
}

/** For a 204-no-content endpoint - apiMutate assumes a JSON ApiResponse body, which a DELETE never returns here. */
export async function apiDelete(path: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const response = await inspectedFetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })

  if (response.status === 401) {
    endSession()
  }

  if (!response.ok) {
    let message = `Request to ${path} failed`
    try {
      const body = (await response.json()) as ApiResponse<never>
      message = body.error ?? message
    } catch {
      // No JSON body on this error - fall back to the generic message.
    }
    throw new ApiError(message, response.status)
  }
}

export interface LoginResult {
  accessToken: string
  expiresIn: number
  role: SessionRole
}

type LoginAttempt = { outcome: 'unauthorized' } | { outcome: 'success'; accessToken: string; expiresIn: number }

async function attemptLogin(path: string, authHeader: string): Promise<LoginAttempt> {
  const response = await inspectedFetch(`${API_BASE_URL}${path}`, { headers: { Authorization: authHeader } })

  if (response.status === 401) {
    return { outcome: 'unauthorized' }
  }

  if (!response.ok) {
    throw new ApiError('Sign in is temporarily unavailable', response.status)
  }

  const body = (await response.json()) as ApiResponse<{ accessToken: string; tokenType: string; expiresIn: number }>
  if (!body.success || !body.data) {
    throw new ApiError('Sign in is temporarily unavailable', response.status)
  }

  return { outcome: 'success', accessToken: body.data.accessToken, expiresIn: body.data.expiresIn }
}

/** How a reset is proved (invitations plan phase 4): the link's token, or the email the request was made for and the code it was sent. */
export type PasswordResetProof = { token: string } | { email: string; code: string }

/** Public endpoint (no bearer token) - completes a reset link's token, or an email and its code, with a new password. */
export async function resetPassword(proof: PasswordResetProof, newPassword: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const response = await inspectedFetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...proof, newPassword }),
  })

  if (!response.ok) {
    let message = 'token' in proof ? 'This reset link is invalid or has expired' : 'The code is wrong or has expired'
    try {
      const body = (await response.json()) as ApiResponse<never>
      message = body.error ?? message
    } catch {
      // No JSON body on this error - fall back to the generic message.
    }
    throw new ApiError(message, response.status)
  }
}

/** Public endpoint (no bearer token) - asks for a reset link and code to be emailed; always accepted, whatever the email. */
export async function requestPasswordReset(email: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }
  const response = await inspectedFetch(`${API_BASE_URL}/auth/password-resets`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!response.ok) {
    let message = 'The request could not be sent'
    try {
      const body = (await response.json()) as ApiResponse<never>
      message = body.error ?? message
    } catch {
      // No JSON body on this error - fall back to the generic message.
    }
    throw new ApiError(message, response.status)
  }
}

/**
 * Tries the operator dashboard credential first, then the facility
 * administrator credential, then a worker's own login - the three account
 * types share one login screen (see Login.tsx), so the server decides which
 * role this username/password belongs to rather than the UI asking the
 * person to pick. The API keeps one email to one kind of login, so the
 * order never hides a worker behind an operator.
 */
export async function verifyCredentials(username: string, password: string): Promise<LoginResult> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const authHeader = `Basic ${btoa(`${username}:${password}`)}`

  const operatorAttempt = await attemptLogin('/auth/login', authHeader)
  if (operatorAttempt.outcome === 'success') {
    return { accessToken: operatorAttempt.accessToken, expiresIn: operatorAttempt.expiresIn, role: 'operator' }
  }

  const facilityAttempt = await attemptLogin('/auth/facility-login', authHeader)
  if (facilityAttempt.outcome === 'success') {
    return { accessToken: facilityAttempt.accessToken, expiresIn: facilityAttempt.expiresIn, role: 'facility_administrator' }
  }

  const workerAttempt = await attemptLogin('/auth/worker-login', authHeader)
  if (workerAttempt.outcome === 'success') {
    return { accessToken: workerAttempt.accessToken, expiresIn: workerAttempt.expiresIn, role: 'worker' }
  }

  throw new ApiError('Incorrect username or password', 401)
}

/** A file the API answers with (a CSV download): the bytes, the filename it named, and the response headers. */
export interface ApiDownload {
  blob: Blob
  fileName: string | null
  headers: Headers
}

/** The filename a `Content-Disposition: attachment; filename="..."` header carries, or null. */
export function fileNameOf(disposition: string | null): string | null {
  const match = /filename="([^"]+)"/.exec(disposition ?? '')
  return match?.[1] ?? null
}

/** Fetches a download as the signed-in person. An error still arrives as the JSON envelope, which is read for its message. */
export async function apiDownload(path: string, accept = 'text/csv'): Promise<ApiDownload> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }
  const response = await inspectedFetch(`${API_BASE_URL}${path}`, { headers: authHeaders({ Accept: accept }) })
  if (response.status === 401) {
    endSession()
  }
  if (!response.ok) {
    const body = await readEnvelope<never>(response, path)
    throw new ApiError(body.error ?? `Request to ${path} failed`, response.status)
  }
  return { blob: await response.blob(), fileName: fileNameOf(response.headers.get('content-disposition')), headers: response.headers }
}

/** Hands a file to the browser to save, under the name given. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Released on the next tick so the click has started the download first.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
